import { useState, useEffect, useCallback } from 'react';
import { chatApi } from '../services/chatapi';
import { AI_PROVIDER_CHANGED_EVENT } from '../utils/aiProvider';

/**
 * Custom hook for managing AI models
 * @returns {Object} Hook properties and methods
 */
const useAiModels = () => {
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // Load models from API. #492: `force` bypasses the shared model cache — used on a
  // provider switch, where the catalogue changes; the mount fetch uses the cache so the
  // chat panel + per-item tools don't each hit /models when they mount together.
  const fetchModels = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await chatApi.getAvailableModels({ force });

      if (response && response.models) {
        setModels(response.models);
        
        // Store metadata about model source
        if (response.metadata) {
          setMetadata(response.metadata);
        }
        
        // Set current model — T1.24: only ever select a model that actually
        // exists in the fetched list, so we never default to one the active
        // provider can't serve (which 404s on first chat). Prefer the stored
        // preference, then the backend's reported current, then the first model.
        const ids = response.models.map(m => m.id);
        const storedModel = localStorage.getItem('preferredAiModel');
        let resolvedModel = null;
        if (storedModel && ids.includes(storedModel)) {
          resolvedModel = storedModel;
        } else if (response.current && ids.includes(response.current)) {
          resolvedModel = response.current;
        } else if (response.models.length > 0) {
          resolvedModel = response.models[0].id;
        }

        if (resolvedModel) {
          setCurrentModel(resolvedModel);
          // #1040: keep localStorage in step with whatever id this hook just
          // resolved. services/chatapi.js's send path reads
          // localStorage.getItem('preferredAiModel') DIRECTLY, not this
          // hook's React state -- previously only an explicit user pick
          // (changeModel) ever wrote that key, so an id auto-resolved here
          // (e.g. the catalogue's `current` after a provider switch made the
          // old stored id disappear from `ids`) was reflected in the UI but
          // never reached the send path, which kept reading the stale value
          // indefinitely. Only write when it actually changed, so the common
          // case (stored preference still valid) causes no extra write.
          if (resolvedModel !== storedModel) {
            try {
              localStorage.setItem('preferredAiModel', resolvedModel);
            } catch (storageError) {
              console.error('Error saving resolved model preference to localStorage:', storageError);
            }
          }
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching AI models:', err);
      setError(err.message || 'Failed to fetch AI models');
    } finally {
      setLoading(false);
    }
  }, []);

  // Change the current model
  const changeModel = useCallback(async (modelId) => {
    setLoading(true);
    setError(null);
    
    try {
      await chatApi.setCurrentModel(modelId);
      setCurrentModel(modelId);
      return true;
    } catch (err) {
      console.error('Error changing AI model:', err);
      setError(err.message || 'Failed to change AI model');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount, and re-fetch when the user switches provider (#106) so
  // the dropdown reflects the newly-selected source's catalogue.
  useEffect(() => {
    fetchModels();
    const onProviderChange = () => {
      // #1040: the re-fetch below is async, and the send path
      // (services/chatapi.js) reads the 'preferredAiModel' localStorage key
      // directly rather than this hook's React state. For as long as the old
      // provider's id sits there, a message sent before the catalogue
      // re-fetch resolves would submit it to the NEW provider verbatim (a
      // bare "gpt-4o-mini" hitting OpenRouter, which requires
      // 'vendor/model'). Clear it immediately on switch so that window finds
      // nothing stale to read; the send path then omits `model` and the
      // backend picks a provider-correct default instead of a known-bad one.
      try {
        localStorage.removeItem('preferredAiModel');
      } catch (storageError) {
        console.error('Error clearing model preference on provider change:', storageError);
      }
      // #1043 (adversarial-review finding 4): clear the STALE previous-
      // provider list alongside the value, not just the value -- leaving
      // `models` in place until the re-fetch below resolves put a real gap on
      // screen where the dropdown still listed the old provider's models
      // while nothing was selected (a visible flicker). And '' (not null) is
      // the sentinel <Select> (ChatInterface.js) accepts without an
      // "out-of-range value" console warning -- MUI warns for any controlled
      // value other than '' that matches none of the rendered <MenuItem>s,
      // which a bare `null` never does.
      setModels([]);
      setCurrentModel('');
      fetchModels({ force: true });  // #492: catalogue changed
    };
    window.addEventListener(AI_PROVIDER_CHANGED_EVENT, onProviderChange);
    return () => window.removeEventListener(AI_PROVIDER_CHANGED_EVENT, onProviderChange);
  }, [fetchModels]);

  return {
    models,
    currentModel,
    loading,
    error,
    metadata,
    fetchModels,
    changeModel,
    isUsingFallbackModels: metadata?.source?.startsWith('hardcoded-'),
    isApiSuccessful: metadata?.source === 'azure-api',
    lastUpdated: metadata?.last_updated ? new Date(metadata.last_updated) : null
  };
};

export default useAiModels;